import {
  Box,
  Button,
  Grid,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import {
  DeleteDialog,
  HeadTable,
  PermissionWrapper,
  TableCaption,
} from 'components';
import PopupAddSitter from 'components/Popups/AC/PopupAddSitter';
import { ButtonApprove, ButtonDelete } from 'components/common/table';
import { NotifyService, getSortItem } from 'helpers';
import {
  CHILDREN_PARENT_DETAIL_TYPE,
  DELETE_DIALOG_TYPE,
  ECLOSE_POPUP,
  GET_CHILDREN_PARENT_DETAIL_TYPE_PARAMS_REQUEST,
  HeadCell,
} from 'models';
import {
  AC_BASE_REQUEST_SITTERS,
  BASE_REQUEST_TYPE,
} from 'models/subscription';
import React, { useMemo, useState } from 'react';
import { acceptSitter } from 'services/subscription';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setParentChildrenListParams } from 'store/reducers/accounts';

const headCells: readonly HeadCell<any>[] = [
  {
    id: 'first_name',
    numeric: false,
    disablePadding: true,
    isSort: false,
    label: 'Sitter Name',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    isSort: false,
    label: 'Email',
  },
  {
    id: 'phone_number',
    numeric: false,
    disablePadding: false,
    isSort: false,
    label: 'Phone',
  },
  {
    id: 'sitter_rate',
    numeric: true,
    disablePadding: false,
    isSort: false,
    label: 'Concierge Sitter Rate',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    isSort: false,
    label: 'Booking status',
  },
];

interface Props {
  requests_detail: BASE_REQUEST_TYPE | null;
  sitter_rate: number;
  onSuccess: any;
}
const Sitters = ({ requests_detail, sitter_rate, onSuccess }: Props) => {
  const dispatch = useAppDispatch();
  const { children } = useAppSelector(
    (state) => state.accountReducer.parents.parent_detail_tab
  );

  const { params } = children;
  const theme = useTheme();

  const [selected, setSelected] = useState<readonly string[]>([]);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [itemSitter, setItemSitter] = useState<AC_BASE_REQUEST_SITTERS>();

  const isAllowedChangeSitter = requests_detail?.ac_base_request_sitters.some(
    (item) => item.status === 'accepted' || item.status === 'rejected'
  );

  const onSetListParams = (
    params: GET_CHILDREN_PARENT_DETAIL_TYPE_PARAMS_REQUEST
  ) => {
    dispatch(setParentChildrenListParams(params));
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: children.metadata.total_pages,
    page: children.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const sortItem = useMemo(() => {
    return getSortItem(params?.sort);
  }, [params]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = children?.records?.map((n: any) => n?.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleRequestSort = (
    _: any,
    property: keyof CHILDREN_PARENT_DETAIL_TYPE
  ) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const onClosePopup = (type: ECLOSE_POPUP) => {
    switch (type) {
      case ECLOSE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setItemSitter(undefined);
        break;
      case ECLOSE_POPUP.ADD:
        setOpenCreatePopup(false);
        break;
    }
  };

  const setOpenDelete = (event: any, data: AC_BASE_REQUEST_SITTERS) => {
    event.stopPropagation();
    setItemSitter(data);
    setOpenDeletePopup(true);
  };

  const setOpenCreate = () => {
    setOpenCreatePopup(true);
  };

  const handleAcceptSitter = async (row: AC_BASE_REQUEST_SITTERS) => {
    await acceptSitter(row.id)
      .then(() => {
        onSuccess();
        NotifyService.success('Success');
      })
      .catch((e) => NotifyService.error(e));
  };

  return (
    <>
      <Box display="flex" mb={'8px'}>
        <PermissionWrapper allowedRoles={['superadmin', 'staff']}>
          <Button
            variant="contained"
            sx={{
              height: 30,
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '100%',
            }}
            onClick={setOpenCreate}
          >
            {isAllowedChangeSitter ? 'Change Sitter' : 'Add Sitter'}
          </Button>
        </PermissionWrapper>
      </Box>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 395px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<CHILDREN_PARENT_DETAIL_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={children?.records.length}
              headCells={headCells}
              onCheckbox={false}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {requests_detail?.ac_base_request_sitters &&
                requests_detail.ac_base_request_sitters.map((row, index) => {
                  if (!row?.sitter) return null;
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                    >
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {row?.sitter?.User.first_name}{' '}
                        {row?.sitter?.User.last_name}
                      </TableCell>

                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {row?.sitter?.User.email}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {row?.sitter?.User.phone_number}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        ${sitter_rate?.toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          textTransform="capitalize"
                        >
                          <Grid
                            sx={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              marginRight: '5px',
                              backgroundColor:
                                row.status === 'accepted'
                                  ? theme.palette.secondary.main
                                  : row.status === 'pending'
                                  ? theme.palette.yellow.light
                                  : '#B6B6B6',
                            }}
                          />
                          <Typography
                            sx={{
                              height: '20px',
                            }}
                          >
                            {row?.status === 'requested for payment'
                              ? 'RFP'
                              : row?.status}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          borderRight: '1px solid #DEE2E6',
                          padding: '18px 12px 17px',
                        }}
                      >
                        <Stack direction="row" spacing="10px">
                          <PermissionWrapper
                            allowedRoles={['superadmin', 'staff']}
                          >
                            <ButtonDelete
                              onClick={(e) => setOpenDelete(e, row)}
                            />
                          </PermissionWrapper>
                          {row.status === 'pending' && (
                            <PermissionWrapper
                              allowedRoles={['superadmin', 'staff']}
                            >
                              <ButtonApprove
                                onClick={() => handleAcceptSitter(row)}
                              >
                                Accept
                              </ButtonApprove>
                            </PermissionWrapper>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          onlyOnePage={true}
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={children.metadata}
          numberItem={requests_detail?.ac_base_request_sitters.length}
          items={items}
        />
      </Stack>

      {openDeletePopup && (
        <DeleteDialog
          name={`${itemSitter?.sitter?.User.first_name} ${itemSitter?.sitter?.User.last_name}`}
          open={openDeletePopup}
          onClose={() => onClosePopup(ECLOSE_POPUP.DELETE)}
          subject={itemSitter?.id}
          onSuccess={onSuccess}
          type={DELETE_DIALOG_TYPE.SITTER_AC}
        />
      )}
      {openCreatePopup && (
        <PopupAddSitter
          open={openCreatePopup}
          handleClosePopup={() => onClosePopup(ECLOSE_POPUP.ADD)}
          subject={requests_detail?.id}
          onSuccess={onSuccess}
          isAllowedChange={isAllowedChangeSitter}
        />
      )}
    </>
  );
};

export default Sitters;
